/**
 * SHARED — billing constants used across auth/billing UI.
 *
 * Single source of truth for anything the customer reads about the trial.
 * Never hardcode the trial length in copy; interpolate TRIAL_DAYS so a change
 * here updates every surface at once.
 *
 * The authoritative model lives in Website/brand-command-stripe-pricing-spec.md.
 * The *functional* trial length is set on the Stripe subscription by the
 * `stripe-checkout` edge function — if you change TRIAL_DAYS here, change it
 * there too (or set the TRIAL_DAYS function secret) or the copy will lie.
 */

/** Free-trial length in days. Locked at 14. */
export const TRIAL_DAYS = 14;

/** Whether a card is required to start a trial. Currently it is not. */
export const TRIAL_REQUIRES_CARD = false;

/** "14-day free trial" — for use mid-sentence. */
export const trialLabel = () => `${TRIAL_DAYS}-day free trial`;

/** Show the "trial ending" banner once this many days remain. */
export const TRIAL_WARNING_DAYS = 7;

// ─────────────────────────────── catalog ────────────────────────────────
//
// Display-only mirror of Website/brand-command-stripe-pricing-spec.md.
// These amounts render the plan picker; they DO NOT determine what anyone is
// charged. Checkout sends only the `lookup_key`, and `stripe-checkout`
// re-resolves the real price from Stripe — so editing these numbers in
// devtools changes the label and nothing else.

export const SEAT_BANDS = [
  { key: "1to5",   label: "1–5 users",   max: 5  },
  { key: "6to10",  label: "6–10 users",  max: 10 },
  { key: "11to20", label: "11–20 users", max: 20 },
];

export const TIERS = [
  {
    key: "starter",
    name: "Starter",
    blurb: "Get on shelf and look sharp doing it.",
    userCap: 10,
    modules: [
      "Distribution Map",
      "Incentive Tracker",
      "Product Specifications",
      "Point of Sales Orders",
      "Product Sample Orders",
      "Retail Product Finder",
    ],
    prices: { "1to5": { monthly: 9900, annual: 99000 }, "6to10": { monthly: 14900, annual: 149000 } },
  },
  {
    key: "growth",
    name: "Growth",
    blurb: "Track what's actually selling through.",
    userCap: 20,
    modules: ["Everything in Starter", "Distributor Orders", "Depletions", "Inventory"],
    prices: {
      "1to5":   { monthly: 29900, annual: 299000 },
      "6to10":  { monthly: 42900, annual: 429000 },
      "11to20": { monthly: 59900, annual: 599000 },
    },
  },
  {
    key: "scale",
    name: "Scale",
    blurb: "Plan ahead and pay distributors on performance.",
    userCap: 20,
    modules: ["Everything in Growth", "Sales Forecasting"],
    prices: {
      "1to5":   { monthly: 69900, annual: 699000 },
      "6to10":  { monthly: 99900, annual: 999000 },
      "11to20": { monthly: 139900, annual: 1399000 },
    },
  },
];

/** Catalog key for a (tier, band, interval). Must match Stripe's lookup_key. */
export const lookupKey = (tier, band, interval) => `${tier}_${band}_${interval}`;

/** 9900 -> "$99". Whole dollars; every catalog amount is a round dollar. */
export const formatUsd = (cents) => `$${(cents / 100).toLocaleString("en-US")}`;

/** Above the largest band we sell self-serve, it's a conversation. */
export const CONTACT_EMAIL = "info@brandcommand.app";
